/**
 * Datadog Implementation (web)
 *
 */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import Constants from 'expo-constants';
import { useEffect, type ReactNode } from 'react';

import { getNativeAppClientInfo } from '#modules/clientContext';

import { vidaVendorConfig } from './vidaVendorConfig';

/**
 * Whether either browser SDK has already been initialized — by us on an earlier mount,
 * or by a **host app that owns Datadog**.
 *
 * `getInitConfiguration()` returns `undefined` before `init` and the resolved config
 * after, so it answers both questions at the SDK level. A module flag of our own only
 * answered the first: `datadogRum`/`datadogLogs` are process-wide singletons shared with
 * whatever else is in the bundle, so a host that initialized first left our flag `false`.
 * We would then call `init` (rejected with "DD_RUM is already initialized", no throw) and
 * carry on to the `setGlobalContextProperty` calls below — which are *not* guarded by the
 * SDK and would land on the host's instance, relabelling `service` / `application` /
 * `release` and stamping our embed keys onto every one of its events, permanently.
 *
 * Checking both SDKs, not just RUM: the block below initializes both and writes global
 * context to both, so if a host owns either one we stay out entirely.
 *
 * @see `@vidahealth/login`'s `utils/analytics/datadog.ts`, which has this bug live inside
 * VidaReactNative today.
 */
const isDatadogAlreadyInitialized = (): boolean =>
  Boolean(datadogRum.getInitConfiguration() || datadogLogs.getInitConfiguration());

/**
 * DatadogProvider component for web
 * Initializes Datadog Browser SDK on mount
 */
export const DatadogProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    if (isDatadogAlreadyInitialized()) {
      console.log('Datadog Web: Already initialized — skipping (a host app may own the SDK)');
      return;
    }

    const environment =
      Constants.expoConfig?.extra?.environment || (__DEV__ ? 'development' : 'production');

    try {
      const isEnabled = vidaVendorConfig().datadog?.enabled;
      if (!isEnabled && !__DEV__) {
        console.log('Datadog Web: Disabled via datadog.enabled flag');
        return;
      }

      const clientToken = vidaVendorConfig().datadog?.clientToken;
      const applicationId = vidaVendorConfig().datadog?.applicationId;

      if (!clientToken) {
        console.warn('Datadog Web: datadog.clientToken is not set');
        return;
      }

      if (!applicationId) {
        console.warn('Datadog Web: datadog.applicationId is not set');
        return;
      }

      const version = Constants.expoConfig?.version || '1.0.0';
      const sessionReplayEnabled = vidaVendorConfig().datadog?.sessionReplayEnabled;

      const appName = Constants.expoConfig?.name || 'Onboarding';
      // Datadog `service` must be distinct from the main Vida app. `expoConfig.name`
      // is the product brand ("Vida"), which VidaReactNative also uses — deriving
      // `service` from it commingles both apps' RUM/logs under `service:Vida`.
      // "Onboarding" is the app-specific identifier; `application` keeps the brand
      // ("Vida") so the product grouping is preserved.
      const serviceName = 'Onboarding';

      datadogRum.init({
        applicationId,
        clientToken,
        site: 'datadoghq.com',
        service: serviceName,
        env: environment,
        version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: sessionReplayEnabled ? 100 : 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask',
        enableExperimentalFeatures: ['feature_flags'],
        // Links RUM resources to their backend APM trace. `apiBaseUrl` resolves per
        // request to https://(staging-)?api.vida.com (see api/axiosConfig.ts), which
        // covers both Apigee proxies (apigee-api-proxy /api/*, auth-proxy /v1/auth/*)
        // under one origin. Apigee must already allow the x-datadog-* headers in CORS
        // (ME-2131) before this can take effect.
        allowedTracingUrls: [
          {
            match: /^https:\/\/(staging-)?api\.vida\.com(?:[:/]|$)/,
            propagatorTypes: ['datadog'],
          },
        ],
        // Full coverage while the per-screen latency map is being built. Separate
        // from sessionSampleRate; lower later once the map is established.
        traceSampleRate: 100,
      });

      datadogLogs.init({
        clientToken,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
      });

      const vidaEnv = vidaVendorConfig().vidaEnv || environment;

      datadogRum.setGlobalContextProperty('service', serviceName);
      datadogRum.setGlobalContextProperty('application', appName);
      datadogRum.setGlobalContextProperty('environment', vidaEnv);
      datadogRum.setGlobalContextProperty('release', version);

      datadogLogs.setGlobalContextProperty('service', serviceName);
      datadogLogs.setGlobalContextProperty('application', appName);
      datadogLogs.setGlobalContextProperty('environment', vidaEnv);
      datadogLogs.setGlobalContextProperty('release', version);

      // Embed launch context (Sparta's setDatadogGlobalContext), stamped on every
      // RUM/Logs event. RUM values are stringified (Logs kept raw) to match the
      // shared convention in @vidahealth/utilos (datadog/setGlobalContext.ts) — also
      // mirrored by datadog.web.ts's setUserProperties — so Sparta-authored monitors
      // faceting on these keys match.
      const { vidaClient, vidaClientVersion, isLoadedInNativeApp } = getNativeAppClientInfo();
      // The boolean is ALWAYS set (false when not embedded) so dashboards can facet
      // on false vs. missing — matches Sparta's helper and our PostHog identify.
      datadogRum.setGlobalContextProperty(
        'is_view_embedded_in_native_app',
        `${isLoadedInNativeApp}`
      );
      datadogLogs.setGlobalContextProperty('is_view_embedded_in_native_app', isLoadedInNativeApp);
      // vida_client / vida_client_version are identifiers that only exist when
      // launched embedded — left absent (not `undefined`) otherwise.
      if (vidaClient) {
        datadogRum.setGlobalContextProperty('vida_client', vidaClient);
        datadogLogs.setGlobalContextProperty('vida_client', vidaClient);
        if (vidaClientVersion) {
          datadogRum.setGlobalContextProperty('vida_client_version', vidaClientVersion);
          datadogLogs.setGlobalContextProperty('vida_client_version', vidaClientVersion);
        }
      }

      if (sessionReplayEnabled) {
        datadogRum.startSessionReplayRecording();
      }

      console.log('Datadog Web: Successfully initialized');
    } catch (error) {
      console.error('Datadog Web: Failed to initialize', error);
    }
  }, []);

  return <>{children}</>;
};
